/**
 * In-place fixes for glTF JSON before THREE.GLTFParser._markDefs runs.
 * Invalid skin joint indices cause: "Cannot set properties of undefined (setting 'isBone')".
 * Invalid mesh indices can break skinned mesh marking the same way.
 */

function jointIndexValid(jointRef, nodeCount, nodeDefs) {
  const idx =
    typeof jointRef === 'string' ? parseInt(jointRef, 10) : jointRef;
  if (!Number.isInteger(idx) || idx < 0 || idx >= nodeCount) return false;
  const def = nodeDefs[idx];
  return def != null && typeof def === 'object';
}

function skinHasOnlyValidJoints(skin, nodeCount, nodeDefs) {
  if (!skin || typeof skin !== 'object') return false;
  const joints = skin.joints;
  if (!Array.isArray(joints) || joints.length === 0) return false;
  for (let i = 0; i < joints.length; i++) {
    if (!jointIndexValid(joints[i], nodeCount, nodeDefs)) return false;
  }
  return true;
}

/**
 * @param {object} json - glTF root object (mutated)
 * @returns {boolean} true if anything was changed
 */
export function sanitizeGltfJsonForLoader(json) {
  if (!json || typeof json !== 'object') return false;

  const nodeDefs = json.nodes;
  if (!Array.isArray(nodeDefs) || nodeDefs.length === 0) return false;

  const meshDefs = json.meshes;
  const meshCount = Array.isArray(meshDefs) ? meshDefs.length : 0;
  const nodeCount = nodeDefs.length;

  let changed = false;

  // Drop invalid mesh references (and dependent skin) on nodes
  if (meshCount > 0) {
    for (let n = 0; n < nodeDefs.length; n++) {
      const node = nodeDefs[n];
      if (!node || node.mesh === undefined) continue;
      const mi =
        typeof node.mesh === 'string' ? parseInt(node.mesh, 10) : node.mesh;
      if (
        !Number.isInteger(mi) ||
        mi < 0 ||
        mi >= meshCount ||
        !meshDefs[mi]
      ) {
        delete node.mesh;
        delete node.skin;
        changed = true;
      }
    }
  }

  const skinDefs = json.skins;
  if (!Array.isArray(skinDefs) || skinDefs.length === 0) return changed;

  const invalidSkinIndices = new Set();
  for (let s = 0; s < skinDefs.length; s++) {
    if (!skinHasOnlyValidJoints(skinDefs[s], nodeCount, nodeDefs)) {
      invalidSkinIndices.add(s);
    }
  }

  if (invalidSkinIndices.size === 0) return changed;

  console.warn(
    '[gltf] Removing invalid skin(s); joint indices reference missing nodes:',
    [...invalidSkinIndices].join(', ')
  );

  const oldToNew = new Map();
  let next = 0;
  for (let s = 0; s < skinDefs.length; s++) {
    if (invalidSkinIndices.has(s)) continue;
    oldToNew.set(s, next++);
  }

  for (let n = 0; n < nodeDefs.length; n++) {
    const node = nodeDefs[n];
    if (!node || typeof node.skin !== 'number') continue;
    if (invalidSkinIndices.has(node.skin)) {
      delete node.skin;
      changed = true;
    } else if (oldToNew.has(node.skin)) {
      const mapped = oldToNew.get(node.skin);
      if (mapped !== node.skin) {
        node.skin = mapped;
        changed = true;
      }
    }
  }

  const newSkins = skinDefs.filter((_, s) => !invalidSkinIndices.has(s));
  if (newSkins.length === 0) {
    delete json.skins;
  } else {
    json.skins = newSkins;
  }

  return true;
}

/**
 * Register on a GLTFLoader instance so JSON is sanitized before parse.
 * @param {import('three/examples/jsm/loaders/GLTFLoader.js').GLTFLoader} loader
 */
export function registerGltfSkinJointSanitizer(loader) {
  if (!loader || typeof loader.register !== 'function') return loader;

  loader.register((parser) => {
    try {
      sanitizeGltfJsonForLoader(parser.json);
    } catch (e) {
      console.warn('[gltf] sanitizeGltfJsonForLoader failed:', e);
    }
    return {
      name: 'SPATIALGRID_sanitize_gltf_skins',
    };
  });

  return loader;
}
